import { Link } from 'react-router-dom';
import { Reveal, Stagger, StaggerItem, HankoStamp, fadeInUp, scaleIn } from './motion';
import { SECURITY, PRICING, CLOSING, useLang, pick } from './content';

export function SecuritySection() {
  const lang = useLang();
  return (
    <section className="relative overflow-hidden bg-ink-900 text-ink-50">
      <div aria-hidden className="absolute -top-24 -left-20 h-80 w-80 rounded-full bg-accent-600/20 blur-3xl" />
      <div aria-hidden className="absolute -bottom-24 -right-20 h-80 w-80 rounded-full bg-citation-500/15 blur-3xl" />
      <div className="relative mx-auto max-w-6xl px-5 sm:px-6 py-16 md:py-20">
        <Reveal>
          <div className="max-w-2xl">
            <div className="text-[12px] uppercase tracking-[0.18em] text-accent-300 font-semibold">{pick(SECURITY.eyebrow, lang)}</div>
            <h2 className="mt-2 display serif-jp text-3xl md:text-4xl font-semibold tracking-tight text-white text-balance">
              {pick(SECURITY.h2, lang)}
            </h2>
          </div>
        </Reveal>

        <Stagger stagger={0.08} className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {SECURITY.groups.map((g) => (
            <StaggerItem key={g.title} variant={fadeInUp}>
              <div className="rounded-xl bg-ink-800/50 border border-ink-700 p-5 h-full">
                <h3 className="text-[14px] font-semibold text-white">{pick(g.title, lang)}</h3>
                <ul className="mt-2 space-y-1">
                  {g.items.map((it) => (
                    <li key={it} className="text-[13px] text-ink-300 flex gap-2">
                      <span className="text-emerald-400 shrink-0">✓</span>
                      {pick(it, lang)}
                    </li>
                  ))}
                </ul>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal variant={scaleIn} delay={0.1}>
          <div className="mt-6 flex flex-wrap gap-2">
            {SECURITY.compliance.map((c) => (
              <span
                key={c.label}
                className={`text-[12px] px-3 py-1.5 rounded-full border ${
                  c.status === 'shipped'
                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20'
                    : 'bg-citation-500/15 text-citation-300 border-citation-500/20'
                }`}
              >
                {pick(c.label, lang)} {c.status === 'underway' && pick('landing.security.underwaySuffix', lang)}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function PricingTeaser() {
  const lang = useLang();
  return (
    <section className="border-t border-ink-100 bg-white">
      <div className="mx-auto max-w-6xl px-5 sm:px-6 py-16 md:py-20">
        <Reveal>
          <div className="max-w-2xl">
            <div className="text-[12px] uppercase tracking-[0.18em] text-accent-600 font-semibold">{pick(PRICING.eyebrow, lang)}</div>
            <h2 className="mt-2 display serif-jp text-3xl md:text-4xl font-semibold tracking-tight text-ink-900 text-balance">
              {pick(PRICING.h2, lang)}
            </h2>
          </div>
        </Reveal>

        <Stagger stagger={0.06} className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {PRICING.plans.map((p) => (
            <StaggerItem key={p.name} variant={fadeInUp}>
              <div className={`card-paper p-5 h-full ${p.highlight ? 'border-accent-500/40 bg-accent-50/40 ring-1 ring-accent-500/20' : ''}`}>
                <div className="display text-[15px] font-semibold text-ink-900">{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="num text-3xl font-semibold text-ink-900">${p.price}</span>
                  {p.price > 0 && <span className="text-[12px] text-ink-500">{pick(PRICING.perMonth, lang)}</span>}
                </div>
                <div className="mt-1 text-[12.5px] text-ink-600">{pick(p.seats, lang)}</div>
                <div className="mt-3 text-[12px] text-ink-500 border-t border-ink-100 pt-2">{pick(p.note, lang)}</div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        <Reveal delay={0.1}>
          <p className="mt-5 text-[13px] text-ink-600 leading-relaxed max-w-3xl">{pick(PRICING.positioning, lang)}</p>
          <Link to="/pricing" className="mt-3 inline-block text-[13px] text-accent-600 hover:underline font-medium">
            {pick('landing.pricing.seeFull', lang)}
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export function ClosingCta() {
  const lang = useLang();
  return (
    <section className="border-t border-ink-100 bg-paper-50/40">
      <div className="mx-auto max-w-5xl px-5 sm:px-6 py-16 md:py-24">
        <Reveal variant={fadeInUp}>
          <div className="relative overflow-hidden rounded-3xl bg-ink-900 text-ink-50 px-7 py-12 md:px-14 md:py-16 text-center">
            <div aria-hidden className="absolute -top-16 left-1/2 -translate-x-1/2 h-56 w-56 rounded-full bg-accent-600/20 blur-3xl" />
            <div className="relative">
              <div className="flex justify-center mb-5">
                <HankoStamp size={52} rotate={-7} />
              </div>
              <h2 className="display serif-jp text-2xl md:text-3xl font-semibold tracking-tight text-white text-balance">
                {pick(CLOSING.h2, lang)}
              </h2>
              <p className="mt-4 text-[14.5px] text-ink-300 leading-relaxed max-w-2xl mx-auto">{pick(CLOSING.body, lang)}</p>
              <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                <Link to="/signup" className="inline-flex items-center px-5 py-2.5 rounded-lg bg-white text-ink-900 text-sm font-semibold hover:bg-paper-100 transition">
                  {pick(CLOSING.ctaPrimary, lang)}
                </Link>
                <Link to="/compare" className="inline-flex items-center px-5 py-2.5 rounded-lg border border-ink-600 text-ink-100 text-sm font-medium hover:bg-ink-800 transition">
                  {pick(CLOSING.ctaSecondary, lang)} →
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
